/**
 * @file faro-telemetry.js
 *
 * Initialises the Grafana Faro Web SDK and exposes the instance as
 * window.wgcFaro for use by wgc-monitoring.js.
 *
 * Depends on:
 *   - js/vendor/faro-web-sdk.iife.js  (weight -202) — exposes window.GrafanaFaroWebSdk
 *   - js/debugRoutine.js              (weight -201) — console suppression on production
 *   - window.wgcFaroConfig            — inline config injected by hook_page_attachments
 *
 * On successful initialisation, window.wgcFaro is set so that wgc-monitoring.js
 * can resolve it lazily at call time. If initialisation is skipped (missing or
 * invalid URL) window.wgcFaro is never set and wgcMonitoring falls back to console.
 *
 * @see js/wgc-monitoring.js
 * @see wgc_monitoring.module
 * @see https://grafana.com/docs/faro/latest/
 */

(function () {

  /** @type {Object} Config injected server-side by hook_page_attachments. */
  var config = window.wgcFaroConfig || {};

  /**
   * Validates the collector URL before it is passed to Faro.
   * Rejects anything that is not an HTTPS request to a known Grafana Cloud
   * collector hostname, guarding against tampered config or XSS-injected values
   * redirecting telemetry to an attacker-controlled endpoint.
   *
   * Valid form: https://faro-collector-prod-{region}.grafana.net/collect/{key}
   *
   * @param {string} url - The URL to validate.
   * @returns {boolean}
   */
  function isValidCollectorUrl(url) {
    // Allow same-origin relative proxy path (production).
    if (url === '/telemetry/rum') return true;
    try {
      var parsed = new URL(url);
      if (parsed.protocol !== 'https:') return false;
      // 542 proxy: *.542.cloud/telemetry/rum
      if (/^[a-z0-9.-]+\.542\.cloud$/.test(parsed.hostname) && parsed.pathname === '/telemetry/rum') return true;
      // Direct Grafana Cloud collector: faro-collector-{region}.grafana.net/collect/{key}
      if (/^faro-collector-[a-z0-9-]+\.grafana\.net$/.test(parsed.hostname)) return true;
      return false;
    } catch (e) {
      return false;
    }
  }

  // Bail out silently if no collector URL is configured or the URL is not trusted.
  if (!config.url || !isValidCollectorUrl(config.url)) return;

  // Save the console methods we want to preserve before Faro wraps them.
  // After init we restore these, leaving only warn and error as Faro wrappers
  // so they are captured in Grafana. This approach is independent of Faro's
  // internal option names, which are minified in the IIFE bundle.
  var savedConsole = {
    log: console.log,
    debug: console.debug,
    info: console.info,
    trace: console.trace,
  };

  /**
   * Initialised Faro instance. Exposed as window.wgcFaro for wgc-monitoring.js.
   *
   * @see https://grafana.com/docs/faro/latest/reference/
   */
  var faro = window.GrafanaFaroWebSdk.initializeFaro({
    /** Grafana Cloud collector endpoint URL. */
    url: config.url,

    /** Application metadata reported with every Faro event. */
    app: {
      name: config.environment === 'local' ? 'drupal_ddev' : (config.appName || 'drupal'),
      version: config.version || '0.0.0',
      environment: config.environment || 'production',
    },

    sessionTracking: {
      /**
       * Persist the session in localStorage so it survives full page
       * reloads and is shared across tabs, instead of Faro's default of
       * a new session per initializeFaro() call. This site is a
       * traditional multi-page Drupal app (not an SPA), so without this
       * every full page navigation was being counted as a new session.
       *
       * @see https://github.com/grafana/faro-web-sdk/issues/129
       */
      persistent: true,

      /**
       * Seed the session's attributes at init time so module versions are
       * baked into the session when it is created or resumed.
       *
       * These MUST NOT be applied via a post-init faro.api.setSession()
       * call: passing attributes without a session id there triggers Faro's
       * internal meta-update handler, which — finding the id undefined —
       * mints a brand-new session on every initializeFaro() call. On a
       * multi-page Drupal site that re-runs this script on every full page
       * load, that re-minted a session per pageview even with
       * persistent: true set, defeating the whole point. Seeding via config
       * lets Faro merge the attributes into the (correctly resumed) session
       * without ever re-creating it.
       *
       * @see https://542digital.atlassian.net/browse/BG-2285
       */
      session: config.modules ? { attributes: config.modules } : undefined,

      /**
       * Session-based sampler. Returns a rate between 0 and 1.
       * A session is either fully captured or fully excluded for its duration.
       *
       * Rates:
       *   1.0 — local (.ddev.site) and 542 staging (.542.cloud) environments
       *   1.0 — authenticated Drupal sessions
       *   0.2 — anonymous production traffic
       *
       * @param {Object} context - Faro sampler context.
       * @returns {number} Sample rate (0–1).
       */
      sampler: function (context) {
        var host = window.location.hostname;
        if (host.indexOf('.ddev.site') > -1 || host.indexOf('.542.cloud') > -1) return 1;
        if (context.metas && context.metas.user && context.metas.user.id) return 1;
        return 0.2;
      },
    },
  });

  // Restore log/debug/info/trace to their pre-Faro state so DevTools attributes
  // calls to the real caller. warn and error remain as Faro wrappers.
  console.log = savedConsole.log;
  console.debug = savedConsole.debug;
  console.info = savedConsole.info;
  console.trace = savedConsole.trace;

  // Module versions (core, wgc_storybook, wgc_user) are seeded into the
  // session via sessionTracking.session.attributes above. They are
  // deliberately NOT applied here with faro.api.setSession(): doing so mints
  // a new session on every page load (see the note on that config option
  // and BG-2285).

  // Expose the instance globally so wgc-monitoring.js can resolve it lazily.
  window.wgcFaro = faro;

}());
